/**
 * Geography Hook
 *
 * Hook for fetching Costa Rica geographic data (provinces, cantons, districts).
 * Provides cascading selection support.
 */

import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'

export interface Province {
  id: number
  code: string
  name: string
}

export interface Canton {
  id: number
  code: string
  name: string
  province_id: number
}

export interface District {
  id: number
  code: string
  name: string
  canton_id: number
}

/**
 * Fetch all provinces
 */
export function useProvinces() {
  return useQuery({
    queryKey: ['geography', 'provinces'],
    queryFn: async () => {
      const response = await api.get('/geography/provinces')
      return response.data as Province[]
    },
    staleTime: Infinity, // Geographic data doesn't change
  })
}

/**
 * Fetch cantons for a specific province
 */
export function useCantons(provinceId: number | null) {
  return useQuery({
    queryKey: ['geography', 'cantons', provinceId],
    queryFn: async () => {
      if (!provinceId) return []
      const response = await api.get(`/geography/cantons?province_id=${provinceId}`)
      return response.data as Canton[]
    },
    enabled: !!provinceId,
    staleTime: Infinity,
  })
}

/**
 * Fetch districts for a specific canton
 */
export function useDistricts(cantonId: number | null) {
  return useQuery({
    queryKey: ['geography', 'districts', cantonId],
    queryFn: async () => {
      if (!cantonId) return []
      const response = await api.get(`/geography/districts?canton_id=${cantonId}`)
      return response.data as District[]
    },
    enabled: !!cantonId,
    staleTime: Infinity,
  })
}

/**
 * Combined hook for cascading geography selection
 */
export function useGeography(
  provinceId: number | null,
  cantonId: number | null
) {
  const provinces = useProvinces()
  const cantons = useCantons(provinceId)
  const districts = useDistricts(cantonId)

  return {
    provinces: provinces.data || [],
    cantons: cantons.data || [],
    districts: districts.data || [],
    isLoading: provinces.isLoading || cantons.isLoading || districts.isLoading,
    isLoadingProvinces: provinces.isLoading,
    isLoadingCantons: cantons.isLoading,
    isLoadingDistricts: districts.isLoading,
  }
}
